import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Trophy, Plus, Users, MapPin, Clock, GameController } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import GamePill from '../components/GamePill';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  registration: 'text-[#39FF14] bg-[#39FF14]/10',
  in_progress: 'text-[#00F0FF] bg-[#00F0FF]/10',
  completed: 'text-[#71717A] bg-[#71717A]/10',
  cancelled: 'text-[#EF4444] bg-[#EF4444]/10'
};

export default function TournamentsPage() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchTournaments();
  }, [statusFilter]);

  const fetchTournaments = async () => {
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await axios.get(`${API_URL}/api/tournaments`, { params, withCredentials: true });
      setTournaments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container" data-testid="tournaments-page">
      {/* Header */}
      <header className="p-6 pb-4 flex items-center justify-between">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <Trophy size={24} weight="duotone" className="inline mr-2 text-[#FFD700]" />
            Tournaments
          </h1>
          <p className="text-[#A1A1AA] text-sm">Compete for glory</p>
        </motion.div>
        <button
          onClick={() => navigate('/create-tournament')}
          className="w-10 h-10 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] flex items-center justify-center"
          data-testid="create-tournament-button"
        >
          <Plus size={20} weight="bold" className="text-white" />
        </button>
      </header>

      {/* Status Filter */}
      <div className="px-6 mb-4">
        <div className="flex gap-2">
          {['', 'registration', 'in_progress', 'completed'].map((status) => (
            <button
              key={status || 'all'}
              onClick={() => setStatusFilter(status)}
              className={`py-1.5 px-3 rounded-lg text-xs font-medium transition-all ${
                statusFilter === status
                  ? 'bg-[#9D4CDD]/20 text-[#9D4CDD] border border-[#9D4CDD]/30'
                  : 'bg-[#181824] text-[#71717A] border border-transparent'
              }`}
            >
              {status === '' ? 'All' : status.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      {/* Tournaments List */}
      <div className="px-6 pb-32">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 rounded-2xl bg-[#0F0F16] animate-pulse"></div>
            ))}
          </div>
        ) : tournaments.length > 0 ? (
          <div className="space-y-3" data-testid="tournaments-list">
            {tournaments.map((tournament, index) => (
              <motion.div
                key={tournament.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => navigate(`/tournaments/${tournament.id}`)}
                className="p-4 rounded-2xl bg-[#0F0F16] border border-white/5 cursor-pointer hover:border-[#FFD700]/30 transition-all"
                data-testid={`tournament-${tournament.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <GamePill game={tournament.game_type} small />
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[tournament.status]}`}>
                        {tournament.status.replace('_', ' ')}
                      </span>
                    </div>
                    <h3 className="text-white font-semibold mt-2">{tournament.name}</h3>
                  </div>
                  {tournament.winner_name && (
                    <div className="flex items-center gap-1 text-[#FFD700] text-xs">
                      <Trophy size={14} weight="fill" />
                      {tournament.winner_name}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                  <div className="flex items-center gap-1">
                    <Clock size={14} weight="duotone" className="text-[#00F0FF]" />
                    {tournament.date} · {tournament.time}
                  </div>
                  <div className="flex items-center gap-1">
                    <Users size={14} weight="duotone" className="text-[#39FF14]" />
                    {tournament.current_players}/{tournament.max_players}
                  </div>
                </div>

                <div className="flex items-center gap-1 text-sm text-[#71717A] mt-2">
                  <MapPin size={14} weight="duotone" className="text-[#9D4CDD]" />
                  {tournament.location}
                </div>

                {tournament.shop_name && (
                  <p className="text-[#00F0FF] text-xs mt-2">
                    Hosted by {tournament.shop_name}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[#71717A]">
            <Trophy size={48} weight="duotone" className="mx-auto mb-4 opacity-50" />
            <p>No tournaments found</p>
            <p className="text-sm mt-1">Create one to get started!</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
